import { useDialogManager } from '../../../../../widgets/dialog';
// SELECT ALL È LO STESSO SEGNO IN OGNI ELENCO (utente, 2026-10-21): quello della libreria Material.
import SelectAllIcon from 'src/shared/material_icons/content/select_all.svg';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import SelectionInspector from './selectionInspector';
import { LightsIcon } from '../../../../../widgets/icons/scene';
import { LightsRailIcon } from './sceneRailIcons';
import styled from 'styled-components';
import { panel } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';

// LA LAMPADINA RESTA AMBRA anche da non selezionata: nell'elenco è l'unico segno che distingue una
// luce da una riga qualsiasi, ed è la stessa regola del prototipo. Il tono viene dal tema e non è
// più il giallo pieno di prima: su fondo chiaro un oro brillante non regge il contrasto che un
// contenuto non testuale deve avere (WCAG 1.4.11), e a tema scuro quel giallo sarebbe una lampada
// accesa in faccia. E il glifo si colora col TRATTO, non riempiendolo: è un disegno di contorno.
const LightGlyph = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${panel.kindLightInk};

    svg {
        width: 16px;
        height: 16px;
        display: block;
    }

    svg:not([stroke]) {
        fill: currentColor;
    }
`;
import { DuplicateIcon as CloneIcon, EyeIcon, EyeOffIcon, PlusIcon as NewIcon, TrashIcon as RemoveIcon } from '../../../../../widgets/icons/scene';
import { BaseTreeNode, TreeComponent, TreeHandle, useTreeNodeManager } from '../tree';
import LightsOverlayComponent from './overlay/LightsOverlayComponent';
import SectionPanel from './sectionPanel';
import { useRemovalPrompt } from './confirmRemoval';
import { freeName, useBornSubject } from './hooks/useBornSubject';
import { SceneEditorAction } from './sceneEditorActionBar';
import { LIGHT_KIND, LIGHT_KIND_HINT, LIGHT_KIND_SHORT, lightIcon, restingDirection } from './lightsEditorConfig';
import { intensityCeiling, placeNewLight, restingPose } from './lightPlacement';

// IL PALCO SI RIDIPINGE QUANDO I MATERIALI SONO PRONTI, non una volta sola. Togliere, aggiungere o
// spegnere una luce obbliga ogni materiale a ricompilare il suo shader, e finché il nuovo non è pronto
// Babylon non disegna la mesh. Il motore disegna solo su richiesta e ne chiede un fotogramma solo,
// che cadeva proprio in quel varco: gli oggetti sparivano fino al clic seguente nel palco.
const repaintWhenReady = (sceneEditor: any) => {
    sceneEditor?.refreshRendering?.();
    sceneEditor?.scene?.executeWhenReady?.(() => sceneEditor?.setRenderingNeeded?.());
};

// QUANTO SFUMA IL BORDO DEL CONO, letto dalla luce: la parte del cono che sfuma, da 0 (bordo netto) a
// 100 (sfuma dal centro). Babylon lo tiene come `innerAngle`, il cono pieno dentro quello intero.
const edgeFalloff = (light: any): number => (light?.angle ? Math.round((1 - (light.innerAngle ?? 0) / light.angle) * 100) : 100);

// Utilities per conversione colori (pattern MaterialOverlayComponent)
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const cleanHex = hex.replace('#', '');
    if (cleanHex.length !== 6) return null;

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number): string => {
    const clamp = (val: number) => Math.max(0, Math.min(255, Math.round(val * 255)));
    return (
        '#' +
        [clamp(r), clamp(g), clamp(b)]
            .map((x) => x.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase()
    );
};

// LightNode interface semplificata seguendo il pattern di ObjectEditor
export interface LightNode extends BaseTreeNode {
    type: 'light' | 'group';
    lightType: string;
    intensity: number;
    diffuseColor: string;
    customIcon: React.ReactNode;
    notTurnableIntoFolder?: boolean;
    visible?: boolean;
    /** Dove sta nella lista: lo scrive il trascinamento, e vive su light.metadata. */
    displayOrder?: number;
    // Proprietà opzionali per diversi tipi di luci
    groundColor?: string;
    position?: [number, number, number];
    direction?: [number, number, number];
    angle?: number;
    falloff?: number;
    // Proprietà per integrazione SceneEditor
    data: {
        // L'oggetto luce BABYLON completo per accesso diretto
        light?: any;
        // Proprietà scomposte per compatibilità
        lightType?: string;
        intensity?: number;
        color?: string;
        position?: { x: number; y: number; z: number };
        direction?: { x: number; y: number; z: number };
        range?: number;
        angle?: number;
        falloff?: number;
        _internalId?: string;
        enabled?: boolean;
        visible?: boolean;
    };
}

interface LightEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    lights?: any[];
    selectedLights?: any[];
    sceneEditor?: any;
    onLightsUpdated?: (lights: any[]) => void;
    onInspectorChange?: (component: React.ReactNode | null) => void;
    /** «Dove sta?» per la luce aperta: passa dallo Scene Editor, perché il pannello vive più a lungo di questo elenco. */
    onRevealLight?: (rowID: string, asRow?: boolean) => void;
    /** «Dove sta?» chiesto da fuori: la riga da portare in vista e far lampeggiare. Con `asRow`, `dataID` è la riga. */
    whereRequest?: { dataID: string; asRow?: boolean; token: number } | null;
    /** La domanda è arrivata all'albero: chi l'ha fatta la può dimenticare. */
    onWhereServed?: (token: number) => void;
}

const LightEditor: FC<LightEditorProps> = ({
    treeRef,
    lights = [],
    selectedLights = [],
    sceneEditor,
    onLightsUpdated,
    onInspectorChange,
    onRevealLight,
    whereRequest,
    onWhereServed
}) => {
    const { showDialog, closeDialog } = useDialogManager();

    // IL SEGNALE È UN CONTATORE, NON UN INTERRUTTORE. Un booleano alzato per dire «rileggi» si
    // perde se è già alzato: React non ridisegna quando il valore non cambia, l'effetto non
    // riparte, e la richiesta svanisce senza che nessuno se ne accorga. Basta un secondo
    // mittente perché i due si annullino a vicenda — uno abbassa mentre l'altro alza — e da lì
    // in poi ogni gesto lavora su una lista ferma: la specie cambiata non compare, e la luce
    // vecchia non muore più, perché la si cerca con l'identificativo di una luce che è già
    // stata sostituita. La scena si riempie di luci orfane che nessuno vede.
    //
    // Un contatore cresce sempre: due richieste ravvicinate restano due.
    const [lightRefresh, setLightRefresh] = useState(0);
    const refreshLights = useCallback(() => setLightRefresh((n) => n + 1), []);

    // L'ELENCO DELLE LUCI VA COPIATO, SEMPRE. `getAllLights()` non costruisce niente: restituisce
    // `scene.lights`, cioè LA STESSA LISTA che il motore tiene e modifica sul posto. Rimetterla in
    // stato dopo averla cambiata è rimettere l'identico riferimento, e React — che confronta per
    // identità — non vede alcun cambiamento e non ridisegna: la scena era già nuova, l'elenco a
    // schermo restava quello di prima fino a che non si cambiava editor e si tornava, che è il solo
    // momento in cui questa lista veniva letta da capo.
    const [localLights, setLocalLights] = useState<any[]>(() => [...(sceneEditor?.getAllLights() || lights || [])]);

    // Ref per tenere i nodi correnti durante l'update
    const currentNodesRef = useRef<LightNode[]>([]);

    // Converti le luci dal SceneEditor in LightNode per il tree
    const convertToLightNodes = useCallback((sceneLights: any[]): LightNode[] => {
        if (!sceneLights || sceneLights.length === 0) {
            return [];
        }

        // L'ORDINE DELLA LISTA È QUELLO CHE L'UTENTE HA DATO, non quello in cui il motore consegna
        // le luci. Sta su `metadata.displayOrder` perché è lì che sopravvive al salvataggio, ma
        // nessuno lo rileggeva costruendo le righe: un ordine salvato non tornava più su, e un
        // trascinamento reggeva solo finché la lista non veniva ricostruita da capo.
        const ordered = [...sceneLights];

        ordered.forEach((light: any, index: number) => {
            if (!light.metadata) light.metadata = {};
            if (light.metadata.displayOrder === undefined) light.metadata.displayOrder = index;
        });

        ordered.sort((a: any, b: any) => (a.metadata.displayOrder ?? 0) - (b.metadata.displayOrder ?? 0));

        return ordered.map((light: any, index: number) => {
            // Determina il tipo di luce
            let lightType = 'point_light';
            if (light.getTypeID) {
                const typeId = light.getTypeID();
                switch (typeId) {
                    case 0:
                        lightType = 'point_light';
                        break;
                    case 1:
                        lightType = 'directional_light';
                        break;
                    case 2:
                        lightType = 'spot_light';
                        break;
                    case 3:
                        lightType = 'hemispheric_light';
                        break;
                }
            }

            // Converti i colori BABYLON in hex
            const getDiffuseColor = () => {
                if (light.diffuse) {
                    return rgbToHex(light.diffuse.r, light.diffuse.g, light.diffuse.b);
                }
                return '#FFFFFF';
            };

            const getGroundColor = () => {
                if (light.groundColor) {
                    return rgbToHex(light.groundColor.r, light.groundColor.g, light.groundColor.b);
                }
                return '#000000';
            };

            return {
                id: light._internalId || light.id || `light-${index}`,
                name: light.name || `Light ${index + 1}`,
                type: 'light' as const,
                // L'ALBERO ORDINA PER QUESTO. Il numero stava già sulla luce ma non arrivava mai al
                // nodo, quindi l'elenco restava nell'ordine in cui il motore 3D le consegna e
                // riordinare non si vedeva.
                displayOrder: light.metadata.displayOrder,
                lightType: lightType,
                intensity: light.intensity ?? 1.0,
                diffuseColor: getDiffuseColor(),
                children: [],
                customIcon: <LightGlyph>{lightIcon(lightType)}</LightGlyph>,
                notTurnableIntoFolder: true,
                visible: light.isEnabled ? light.isEnabled() : true,
                position: light.position ? [light.position.x, light.position.y, light.position.z] : [0, 0, 0],
                direction: light.direction ? [light.direction.x, light.direction.y, light.direction.z] : [0, -1, 0],
                angle: light.angle ? (light.angle * 180) / Math.PI : 30, // Convertito in gradi per UI
                falloff: edgeFalloff(light),
                groundColor: getGroundColor(),
                data: {
                    // Salva l'oggetto luce completo per accesso diretto
                    light: light,
                    lightType: lightType,
                    intensity: light.intensity ?? 1.0,
                    color: getDiffuseColor(), // Usa il colore convertito
                    position: light.position ? { x: light.position.x, y: light.position.y, z: light.position.z } : { x: 0, y: 0, z: 0 },
                    direction: light.direction ? { x: light.direction.x, y: light.direction.y, z: light.direction.z } : { x: 0, y: -1, z: 0 },
                    range: light.range ?? 100,
                    angle: light.angle ? (light.angle * 180) / Math.PI : 30, // Convertito in gradi
                    falloff: edgeFalloff(light),
                    _internalId: light._internalId,
                    enabled: light.isEnabled ? light.isEnabled() : true,
                    visible: light.isEnabled ? light.isEnabled() : true
                }
            };
        });
    }, []);

    // Ottieni le luci iniziali convertite - USA QUELLI DALLO SCENE EDITOR
    const lightNodes = useMemo(() => {
        const nodes = convertToLightNodes(localLights);
        // Aggiorna il ref per usarlo durante l'update
        currentNodesRef.current = nodes;
        return nodes;
    }, [localLights, convertToLightNodes]);

    // Ref per tenere riferimento alla funzione updateNode
    const updateNodeRef = useRef<((id: string, updates: Partial<LightNode>) => void) | null>(null);

    // DUE PONTI VERSO IL CORPO DEL COMPONENTE. Il pannello di una luce si costruisce dentro una
    // funzione senza dipendenze — è voluto: si rifà solo quando cambia la luce, non a ogni respiro
    // dello schermo — e da lì non si vedono né lo stato della lista né le funzioni che lo cambiano.
    const setVisibilityRef = useRef<((id: string, visible: boolean) => void) | null>(null);
    const changeTypeRef = useRef<((node: LightNode, lightType: string) => void) | null>(null);
    // E un terzo, verso lo Scene Editor: la freccetta «dove sta» del pannello.
    const revealLightRef = useRef(onRevealLight);
    revealLightRef.current = onRevealLight;

    // Funzione per creare il componente overlay
    const createLightOverlay = useCallback((node: LightNode) => {
        return () => {
            // I nodi gruppo non hanno overlay
            if (node.type === 'group') {
                return null;
            }

            // Usa la luce BABYLON reale direttamente
            const light = node.data.light;

            if (!light) {
                return <div>Light not found</div>;
            }

            // Callback per aggiornare la luce - pattern ObjectOverlayComponent completo
            const handleLightUpdate = (updates: any) => {
                // 1. Aggiorna la luce BABYLON direttamente
                if (light) {
                    // Nome
                    if (updates.name && light.name !== updates.name) {
                        light.name = updates.name;
                    }

                    // Intensità
                    if (updates.intensity !== undefined && light.intensity !== updates.intensity) {
                        light.intensity = updates.intensity;
                    }

                    // ACCESA O SPENTA. È lo stesso dato che l'occhio della lista commuta: quello che si
                    // scrive qui deve arrivare anche là, o i due segni raccontano due storie — ed è
                    // esattamente quello che succedeva.
                    //
                    // NON BASTA SCRIVERE IL NODO: l'albero non legge il campo `visible` che gli si
                    // scrive addosso, lo RICALCOLA da un elenco suo di righe spente. Scritto solo il
                    // nodo, l'occhio restava acceso su una luce spenta.
                    if (updates.active !== undefined) {
                        if (typeof light.setEnabled === 'function') light.setEnabled(updates.active);
                        setVisibilityRef.current?.(node.id, updates.active);
                        repaintWhenReady(sceneEditor);
                    }

                    // Posizione (per point e spot lights)
                    if (updates.position && light.position) {
                        light.position.x = updates.position[0];
                        light.position.y = updates.position[1];
                        light.position.z = updates.position[2];
                    }

                    // Direzione (per directional e spot lights)
                    if (updates.direction && light.direction) {
                        light.direction.x = updates.direction[0];
                        light.direction.y = updates.direction[1];
                        light.direction.z = updates.direction[2];
                    }

                    // Colori (diffuse/specular) - convertiti da hex a Vector3
                    if (updates.diffuseColor && light.diffuse) {
                        const rgb = hexToRgb(updates.diffuseColor);
                        if (rgb) {
                            light.diffuse.r = rgb.r / 255;
                            light.diffuse.g = rgb.g / 255;
                            light.diffuse.b = rgb.b / 255;
                        }
                    }

                    // Ground color per hemispheric lights
                    if (updates.groundColor && light.groundColor) {
                        const rgb = hexToRgb(updates.groundColor);
                        if (rgb) {
                            light.groundColor.r = rgb.r / 255;
                            light.groundColor.g = rgb.g / 255;
                            light.groundColor.b = rgb.b / 255;
                        }
                    }

                    // Angolo per spot lights (convertito da gradi a radianti). Il bordo che sfuma è una
                    // parte del cono, e allargandolo resta la stessa parte.
                    if (updates.angle !== undefined && light.angle !== undefined) {
                        const share = light.angle ? (light.innerAngle ?? 0) / light.angle : 0;
                        light.angle = (updates.angle * Math.PI) / 180;
                        if (light.innerAngle !== undefined) light.innerAngle = light.angle * share;
                    }

                    // IL BORDO DEL CONO. La caduta glTF è quella che legge `innerAngle`; cambiarla non
                    // avvisa i materiali da sola, e senza quell'avviso il faretto resta com'era.
                    if (updates.falloff !== undefined && light.innerAngle !== undefined) {
                        if (light.falloffType !== BABYLON.Light.FALLOFF_GLTF) {
                            light.falloffType = BABYLON.Light.FALLOFF_GLTF;
                            light._markMeshesAsLightDirty?.();
                            repaintWhenReady(sceneEditor);
                        }
                        light.innerAngle = light.angle * (1 - updates.falloff / 100);
                    }
                }

                // 2. Aggiorna il nodo nel tree (per sincronizzare nome visibile e altri valori)
                if (updateNodeRef.current) {
                    const nodeUpdates: any = {};

                    if (updates.name) {
                        nodeUpdates.name = updates.name;
                    }
                    if (updates.intensity !== undefined) {
                        nodeUpdates.intensity = updates.intensity;
                    }
                    if (updates.diffuseColor) {
                        nodeUpdates.diffuseColor = updates.diffuseColor;
                    }

                    if (updates.groundColor) {
                        nodeUpdates.groundColor = updates.groundColor;
                    }
                    if (updates.position) {
                        nodeUpdates.position = updates.position;
                    }
                    if (updates.direction) {
                        nodeUpdates.direction = updates.direction;
                    }
                    if (updates.angle !== undefined) {
                        nodeUpdates.angle = updates.angle;
                    }
                    if (updates.falloff !== undefined) {
                        nodeUpdates.falloff = updates.falloff;
                    }
                    if (updates.active !== undefined) {
                        nodeUpdates.visible = updates.active;
                    }

                    if (Object.keys(nodeUpdates).length > 0) {
                        updateNodeRef.current(node.id, nodeUpdates);
                    }
                }

                // 3. Renderizza la scena per mostrare i cambiamenti
                if (sceneEditor && sceneEditor.render) {
                    sceneEditor.render();
                }
            };

            // Prepara i dati della luce per l'overlay - usa i valori dal nodo che sono già convertiti
            const lightData = {
                id: light._internalId || light.id,
                name: light.name,
                lightType: node.lightType,
                active: light.isEnabled ? light.isEnabled() : true,
                intensity: light.intensity ?? node.intensity,
                diffuseColor: node.diffuseColor, // Già convertito in hex da convertToLightNodes

                groundColor: node.groundColor, // Già convertito in hex da convertToLightNodes
                position: light.position ? ([light.position.x, light.position.y, light.position.z] as [number, number, number]) : node.position,
                direction: light.direction ? ([light.direction.x, light.direction.y, light.direction.z] as [number, number, number]) : node.direction,
                angle: light.angle ? (light.angle * 180) / Math.PI : node.angle, // Convertito in gradi per UI
                falloff: edgeFalloff(light),
                // Il cursore arriva fin dove la scena lo chiede, e mai sotto il valore che la luce ha già.
                intensityMax: Math.max(intensityCeiling(light, node.lightType), Math.ceil(light.intensity ?? 0)),
                restingPose: restingPose(light, node.lightType) ?? undefined
            };

            return (
                <LightsOverlayComponent
                    light={lightData}
                    onUpdate={handleLightUpdate}
                    onChangeType={(lightType) => changeTypeRef.current?.(node, lightType)}
                    onReveal={() => revealLightRef.current?.(node.id, true)}
                />
            );
        };
    }, []);

    const { nodes, selectedIds, treeState, filter, setFilter, findNodeById, updateNode, cloneNodes, removeNodes, groupNodes, setNodes, filteredData } =
        useTreeNodeManager<LightNode>(lightNodes, createLightOverlay);

    // Aggiungi il componente overlay direttamente ai nodi del treeState (come objectEditor)
    useEffect(() => {
        // SEMPRE, NON «SE MANCA». Un nodo ricostruito — ed è quello che succede cambiando la specie
        // di una luce, che in Babylon vuol dire rifarla — si portava dietro il pannello costruito
        // sulla luce di prima: a schermo la scena cambiava e l'Inspector no, fino a che non si
        // sceglieva un'altra riga e si tornava indietro.
        treeState.nodes.forEach((node) => {
            node.component = createLightOverlay(node);
        });
    }, [treeState.nodes, createLightOverlay]);

    // Aggiorna il ref con la funzione updateNode una volta disponibile
    useEffect(() => {
        updateNodeRef.current = updateNode;
        setVisibilityRef.current = treeState.setVisibility;
    }, [updateNode, treeState.setVisibility]);

    // L'OCCHIO SI SEMINA DALLA LUCE a ogni rilettura dell'elenco. L'albero tiene un elenco suo di righe
    // spente, che rinasce vuoto quando la sezione si rimonta — e si rimonta a ogni cambio di rail: una
    // luce spenta tornava con l'occhio acceso, e «Rendering» diceva il contrario. Non conosceva nemmeno
    // gli id nuovi, quelli di una luce rifatta cambiandole la specie o duplicata. Sul nodo `visible` è
    // già `isEnabled()`, cioè la stessa risposta di «Active».
    const seedVisibility = treeState.setVisibility;

    useEffect(() => {
        lightNodes.forEach((node) => seedVisibility(node.id, node.visible !== false));
    }, [lightNodes, seedVisibility]);

    // «DOVE STA?» — la riga si porta in vista e lampeggia, senza cambiare né la scelta né il pannello.
    // La domanda arriva dallo Scene Editor, anche quando la si fa da un'altra sezione; e si dice che è
    // arrivata, perché a una domanda si risponde una volta.
    const [blinkRequest, setBlinkRequest] = useState<{ nodeId?: string; dataID?: string; token: number } | null>(null);

    useEffect(() => {
        if (!whereRequest) return;

        const target = whereRequest.asRow ? { nodeId: whereRequest.dataID } : { dataID: whereRequest.dataID };
        setBlinkRequest((previous) => ({ ...target, token: (previous?.token ?? 0) + 1 }));
        onWhereServed?.(whereRequest.token);
    }, [whereRequest, onWhereServed]);

    // LA LISTA SI RILEGGE DAL MOTORE, che è l'unico a sapere quali luci esistono davvero.
    useEffect(() => {
        if (!sceneEditor) return;

        setLocalLights([...sceneEditor.getAllLights()]);
    }, [lightRefresh, sceneEditor]);

    // LE LUCI POSSONO CAMBIARE DA FUORI — il modello che finisce di caricarsi, un'altra parte
    // della pagina — e questo elenco non se ne accorgerebbe: si costruisce una volta e poi lo
    // aggiorna solo chi lo modifica da qui. Finché ogni modifica della scena rimontava la sezione
    // da capo la cosa non si vedeva, perché rinascendo rileggeva tutto; ora la sezione sopravvive
    // ai cambiamenti — è ciò che tiene aperto il pannello — e il segnale va ascoltato.
    //
    // SI RILEGGE DAL MOTORE, non si copia la lista che arriva: chi ce la passa la filtra e la
    // ordina a modo suo, e due liste diverse per la stessa cosa è come non averne nessuna.
    useEffect(() => {
        refreshLights();
    }, [lights, refreshLights]);

    // Callback per aggiornare le luci nel SceneEditor quando il tree cambia
    const handleTreeDataChanged = useCallback(
        (updatedNodes: LightNode[]) => {
            if (onLightsUpdated) {
                const sceneLights = updatedNodes.map((node) => node.data.light).filter(Boolean);
                onLightsUpdated(sceneLights);
            }
        },
        [onLightsUpdated]
    );

    // La riga nuova compare solo dopo che la scena è stata riletta: si dichiara il nome che sta
    // per comparire, e quando compare diventa il soggetto.
    // APRIRE UNA RIGA NON È SCEGLIERLA. La colonna di destra non la scrive chi seleziona: la
    // scrive l'albero, che tiene da parte QUALE riga è aperta e ne rifà il pannello ogni volta
    // che qualcosa si muove — è così che rinominare una riga aggiorna la testata. Consegnando il
    // pannello a mano e lasciando indietro quel segno, il pannello diventava una fotografia: da
    // lì in avanti l'albero continuava a rifare quello della riga di prima.
    const expectLight = useBornSubject(nodes, (born) => {
        treeState.setSelectedIds(new Set([born.id]));
        treeState.showOverlay(born.id);
    });

    // Funzione per forzare il refresh del tree dalle luci (come prepareMaterials in materialsEditor)
    const prepareLights = useCallback(() => {
        if (sceneEditor) {
            refreshLights();

            const updatedLights = sceneEditor.getAllLights();

            // Notifica il componente padre con le luci aggiornate
            if (onLightsUpdated) {
                onLightsUpdated(updatedLights);
            }
        }
    }, [sceneEditor, onLightsUpdated, refreshLights]);

    // Crea un treeState per il TreeComponent che usa filteredData quando c'è un filtro attivo
    const treeStateForComponent = useMemo(() => {
        const sourceNodes = filter ? filteredData : treeState.nodes;
        return {
            ...treeState,
            nodes: sourceNodes
        };
    }, [treeState, filteredData, filter]);

    // LE QUATTRO SPECIE NEL MENU DI ADD (prototipo D166): nome e icona della specie, e una riga che la spiega.
    const lightsOptions = ['point_light', 'spot_light', 'directional_light', 'hemispheric_light'].map((value) => ({
        value,
        label: LIGHT_KIND[value],
        description: LIGHT_KIND_HINT[value]
    }));

    // LA LUCE NASCE SUBITO, DEL TIPO CHE SCEGLI. Prima «Add light» apriva un modulo con due campi:
    // il nome e il tipo. Il nome però non è una decisione — è una cosa che si aggiusta guardando
    // l'elenco — mentre il tipo sì: una luce puntiforme e una direzionale sono oggetti diversi, e
    // in Babylon il tipo non si cambia dopo. Così la scelta che conta sta nel menu di «Add», e il
    // nome arriva libero: la luce esiste, è il soggetto, e la si rinomina dove la si legge.
    const addLight = useCallback(
        (option: { value: string; label: string }) => {
            treeState.closeOverlay();
            if (!sceneEditor || !sceneEditor.createLight) return;

            // NASCE COL NOME DELLA SPECIE (prototipo): «Point light», poi «Point light 2».
            const name = freeName(nodes, LIGHT_KIND[option.value] ?? T._('Light', 'Admin'));

            // Mappa i tipi di luce ai valori BABYLON con la prima lettera maiuscola
            const lightTypeParts = option.value.replace('_light', '').split('_');
            const babylonLightType = lightTypeParts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('');

            const born: any = sceneEditor.createLight(name, babylonLightType);

            // UNA LUCE CHE PUNTA DA NESSUNA PARTE NON ILLUMINA: il motore fa nascere faretti, direzionali
            // ed emisferiche col vettore a zero, e un faretto appena aggiunto non faceva niente finché
            // non gli si scriveva una direzione a mano.
            if (born?.direction && !born.direction.x && !born.direction.y && !born.direction.z) {
                const [x, y, z] = restingDirection(option.value);
                Object.assign(born.direction, { x, y, z });
            }

            // E NASCE DOVE SI VEDE: sopra il modello, con un'intensità che lo raggiunge (`lightPlacement`).
            placeNewLight(born, option.value);

            repaintWhenReady(sceneEditor);

            // Aggiorna dal SceneEditor che ora contiene la nuova luce
            prepareLights();
            expectLight(name);
        },
        [treeState, sceneEditor, nodes, prepareLights, expectLight]
    );

    // DOPO IL CAMBIO DI SPECIE LA LUCE È UN'ALTRA, con un altro identificativo: la selezione punta
    // a una riga che non esiste più e il pannello si chiuderebbe in faccia a chi ha appena scelto.
    // Si tiene da parte la luce appena nata e la si sceglie appena la sua riga compare.
    //
    // SI TIENE L'OGGETTO, NON IL SUO IDENTIFICATIVO. Per nome non si può — a differenza di «Add»,
    // dove il nome è libero per costruzione, qui il nome è lo stesso e per un istante lo portano in
    // due — e per identificativo si dipenderebbe da come l'elenco lo ricava, che è una regola
    // scritta altrove e che può cambiare. La luce, invece, è la stessa cosa da tutt'e due i lati.
    const [bornLight, setBornLight] = useState<any>(null);

    useEffect(() => {
        if (!bornLight) return;

        const born = nodes.find((node) => node.data?.light === bornLight);

        if (!born) return;

        setBornLight(null);

        // LA RIGA APERTA SI RIDICHIARA, e si ridichiara con lo STATO. Cambiando specie la luce di prima
        // muore: il suo identificativo sparisce, e la risincronizzazione dell'albero lascia cadere la
        // riga aperta perché non è più fra i vivi. Da quel momento la colonna di destra non ha più un
        // soggetto da rifare — e resta a disegnare l'ultima cosa che le era stata consegnata, che è
        // esattamente il pannello della luce che non c'è più.
        //
        // NON CON LA MANIGLIA DELL'ALBERO: `selectAndOpenOverlay` è costruita una volta e tenuta in un
        // riferimento, quindi si porta dentro l'elenco com'era quando è nata. Chiamata subito dopo una
        // ricostruzione cerca la luce nuova in un elenco vecchio, non la trova, ed esce in silenzio.
        treeState.setSelectedIds(new Set([born.id]));
        treeState.showOverlay(born.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bornLight, nodes]);

    // CAMBIARE LA SPECIE DI UNA LUCE VUOL DIRE RIFARLA. In Babylon il tipo non è un attributo: una
    // puntiforme e un faretto sono due classi diverse, e non esiste un metodo che trasformi l'una
    // nell'altra. Quindi si fa l'unica cosa onesta: si prende nota di tutto quello che la luce è, la
    // si toglie, se ne crea una del tipo nuovo con lo stesso nome, e le si rimette addosso ciò che
    // il tipo nuovo sa portare.
    //
    // QUELLO CHE IL TIPO NUOVO NON HA SI PERDE, e non c'è modo di evitarlo: il cono di un faretto
    // non significa niente su una puntiforme. Il resto — nome, intensità, colori, posizione,
    // direzione, accesa o spenta, posto nell'elenco — sopravvive.
    //
    // CAMBIA L'IDENTIFICATIVO, perché lo assegna il motore alla nascita e non lo accetta da fuori.
    // Oggi nessuno tiene il riferimento a una luce per id: il giorno in cui il Composer lo farà,
    // questo è il posto da guardare.
    // IL SOGGETTO ARRIVA, NON SI CERCA. Prima da qui si ripescava il nodo per identificativo,
    // e la ricerca poteva mancare — bastava che l'elenco fosse in mezzo a una ricostruzione — e
    // mancava in SILENZIO: nessun errore, il menu tornava indietro da solo, e chi guardava vedeva
    // un comando che non fa niente. Il pannello di una luce quel nodo ce l'ha in mano: passarlo
    // toglie di mezzo sia la ricerca sia il momento in cui potrebbe fallire.
    const changeLightType = useCallback(
        (node: LightNode, lightType: string) => {
            const light = node?.data?.light;
            if (!sceneEditor || !light || lightType === node?.lightType) return;

            const kind = lightType.replace('_light', '');
            const babylonType = kind.charAt(0).toUpperCase() + kind.slice(1);

            const color = (value: any) => (value ? { r: value.r, g: value.g, b: value.b } : null);
            const vector = (value: any) => (value ? { x: value.x, y: value.y, z: value.z } : null);

            const before = {
                name: light.name,
                intensity: light.intensity,
                diffuse: color(light.diffuse),
                specular: color(light.specular),
                ground: color(light.groundColor),
                position: vector(light.position),
                direction: vector(light.direction),
                angle: light.angle,
                exponent: light.exponent,
                enabled: typeof light.isEnabled === 'function' ? light.isEnabled() : true,
                displayOrder: light.metadata?.displayOrder
            };

            sceneEditor.deleteLight(light.id);

            const born: any = sceneEditor.createLight(before.name, babylonType);

            // Rifare una luce fa ricompilare ogni materiale: il palco va ridipinto a shader pronti, o le
            // mesh restano invisibili fino al clic seguente.
            repaintWhenReady(sceneEditor);

            if (!born) {
                prepareLights();
                return;
            }

            if (typeof before.intensity === 'number') born.intensity = before.intensity;
            if (before.diffuse && born.diffuse) Object.assign(born.diffuse, before.diffuse);
            if (before.specular && born.specular) Object.assign(born.specular, before.specular);
            if (before.ground && born.groundColor) Object.assign(born.groundColor, before.ground);
            if (before.position && born.position) Object.assign(born.position, before.position);

            // UNA DIREZIONE NULLA NON È UNA DIREZIONE: il motore fa nascere le luci direzionali con il
            // vettore a zero, e una luce che punta da nessuna parte non illumina. Se non c'è niente da
            // riportare — si veniva da una puntiforme, che non ha direzione, o da una luce mai puntata —
            // la si mette a riposo: in giù, o in su se è un'emisferica.
            const aimed = before.direction && (before.direction.x || before.direction.y || before.direction.z) ? before.direction : null;
            const [restX, restY, restZ] = restingDirection(lightType);
            if (born.direction) Object.assign(born.direction, aimed ?? { x: restX, y: restY, z: restZ });

            if (typeof before.angle === 'number' && born.angle !== undefined) born.angle = before.angle;
            if (typeof before.exponent === 'number' && born.exponent !== undefined) born.exponent = before.exponent;
            if (typeof born.setEnabled === 'function') born.setEnabled(before.enabled);

            if (!born.metadata) born.metadata = {};
            if (before.displayOrder !== undefined) born.metadata.displayOrder = before.displayOrder;

            // DA UNA LUCE SENZA POSIZIONE A UNA CHE CE L'HA, la si mette dove si vede, come una nuova: una
            // direzionale o un'emisferica non hanno un posto né un'intensità che valgano per una puntiforme.
            // Fra puntiforme e faretto invece si tiene tutto: quel posto l'ha scelto qualcuno.
            const hadPlace = node.lightType === 'point_light' || node.lightType === 'spot_light';
            if (!hadPlace) placeNewLight(born, lightType);

            prepareLights();
            setBornLight(born);
        },
        [sceneEditor, prepareLights]
    );

    useEffect(() => {
        changeTypeRef.current = changeLightType;
    }, [changeLightType]);

    // «Seleziona tutto» sta fra i comandi, come negli oggetti: come casella sopra l'albero era
    // l'unico controllo di quel genere nella colonna e sfalsava l'allineamento fra le sezioni.
    const selectableIds: string[] = treeState.flatVisibleList.map((node: any) => node.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => treeState.selectedIds.has(id));

    // NELLA BARRA RESTA CIÒ CHE NON HA UN SOGGETTO: «Add light» ne fa nascere una che ancora non
    // c'è, «Select all» prende l'elenco intero. Gli altri comandi sono scesi nel «…» della riga.
    // Una sola conferma per togliere, in tutte le liste: il testo lo compone lei.
    const askRemove = useRemovalPrompt();

    // ACCENDERE E SPEGNERE UNA LUCE, da un posto solo. È lo stesso dato in tre punti — l'occhio
    // della riga, i due comandi del mucchio, l'interruttore «Active» del pannello — e tre strade
    // per scriverlo sono tre posti in cui sbagliarlo.
    //
    // Per una luce l'occhio NON è «Visible»: una luce spenta non illumina, e togliere una luce
    // dalla resa è proprio quello che ci si aspetta cliccandolo. Il gizmo nel palco è un altro
    // discorso, e non ce lo abbiamo.
    const applyLightEnabled = useCallback(
        (ids: string[], next: boolean) => {
            ids.forEach((id) => {
                const light = findNodeById(id)?.data?.light;
                if (light && typeof light.setEnabled === 'function') light.setEnabled(next);
            });

            // Il palco non dipinge di continuo: senza questo la luce si spegne nel modello e non
            // nella fotografia. E accendere o spegnere fa ricompilare i materiali, quindi il
            // fotogramma buono è quello a shader pronti.
            repaintWhenReady(sceneEditor);
        },
        [findNodeById, sceneEditor]
    );

    // L'albero ha già commutato il nodo quando arriva qui (`toggleVisibility`): a noi resta la
    // luce vera.
    const handleToggleLight = useCallback((nodeId: string, nextVisible: boolean) => applyLightEnabled([nodeId], nextVisible), [applyLightEnabled]);

    const tools: SceneEditorAction[] = [
        {
            name: T._('Add', 'Admin'),
            tooltip: T._('A new light in this scene', 'Admin'),
            icon: <NewIcon />,
            kind: 'add',
            // Il bottone apre il menu: senza voci non farebbe niente, ed è il caso in cui il
            // motore 3D non c'è ancora.
            onClick: () => treeState.closeOverlay(),
            menu: lightsOptions.map((option) => ({
                name: option.label,
                tooltip: option.description,
                icon: lightIcon(option.value),
                onClick: () => addLight(option)
            }))
        },
        {
            name: allSelected ? T._('Select none', 'Admin') : T._('Select all', 'Admin'),
            tooltip: T._('Everything visible in this list', 'Admin'),
            icon: <SelectAllIcon />,
            kind: 'select',
            active: allSelected,
            disabled: selectableIds.length === 0,
            onClick: () => treeState.setSelectedIds(new Set(allSelected ? [] : selectableIds))
        }
    ];

    // UN MOTORE, DUE PORTE — come negli oggetti: il «…» passa la riga o, su una riga scelta insieme
    // ad altre, la selezione; il pannello del mucchio passa le righe prese. La selezione non si
    // guarda mai da qui dentro.
    const subjectCommands = useCallback(
        (ids: string[]): SceneEditorAction[] => {
            if (!ids.length) return [];

            const commands: SceneEditorAction[] = [
                {
                    name: T._('Clone', 'Admin'),
                    tooltip: T._('A copy in this scene', 'Admin'),
                    icon: <CloneIcon />,
                    kind: 'action',
                    onClick: () => {
                        treeState.closeOverlay();

                        ids.forEach((selectedId) => {
                            const selectedNode = findNodeById(selectedId);
                            if (selectedNode && selectedNode.type === 'light') {
                                // «<nome> copy», libero fra quelli che ci sono (prototipo): come i cloni di oggetti e materiali.
                                const clonedName = freeName(nodes, stringFormat(T._('{0} copy', 'Admin'), selectedNode.name));

                                // Se abbiamo SceneEditor e la luce originale
                                if (sceneEditor && sceneEditor.cloneLight && selectedNode.data?.light?.id) {
                                    sceneEditor.cloneLight(selectedNode.data.light.id, undefined, clonedName);
                                }
                            }
                        });

                        repaintWhenReady(sceneEditor);

                        // CORREZIONE: Aggiorna dal SceneEditor che ora contiene le luci clonate
                        prepareLights();
                    }
                }
            ];

            // I DUE INTERRUTTORI DEL MUCCHIO, e solo del mucchio: su una riga sola a spegnere basta
            // l'occhio che le sta accanto, e un comando nel «…» per quello che si fa con un clic è
            // la stessa cosa detta due volte.
            if (ids.length > 1) {
                const turn = (next: boolean) => () => {
                    treeState.closeOverlay();
                    ids.forEach((id) => treeState.setVisibility(id, next));
                    applyLightEnabled(ids, next);
                };

                commands.push(
                    {
                        name: T._('Turn on', 'Admin'),
                        tooltip: T._('Back into the render', 'Admin'),
                        icon: <EyeIcon />,
                        kind: 'action',
                        onClick: turn(true)
                    },
                    {
                        name: T._('Turn off', 'Admin'),
                        tooltip: T._('Out of the render', 'Admin'),
                        icon: <EyeOffIcon />,
                        kind: 'action',
                        onClick: turn(false)
                    }
                );
            }

            commands.push({
                name: T._('Remove', 'Admin'),
                tooltip: T._('Out of this scene', 'Admin'),
                icon: <RemoveIcon />,
                kind: 'danger',
                onClick: () => {
                    // Il pannello si chiude solo se si conferma: chi rinuncia lo ritrova com'era.
                    askRemove({
                        names: ids.map((id) => findNodeById(id)?.name).filter((name): name is string => !!name),
                        one: T._('light', 'Admin'),
                        many: T._('lights', 'Admin'),
                        onConfirm: () => {
                            treeState.closeOverlay();
                            // Se abbiamo SceneEditor, rimuovi anche da lì
                            if (sceneEditor && sceneEditor.deleteLight) {
                                ids.forEach((selectedId) => {
                                    const selectedNode = findNodeById(selectedId);
                                    if (selectedNode && selectedNode.data?.light?.id) {
                                        sceneEditor.deleteLight(selectedNode.data.light.id);
                                    }
                                });

                                // Togliere una luce è lo stesso varco del cambio di specie.
                                repaintWhenReady(sceneEditor);
                            }

                            // Rimuovi dal tree state
                            removeNodes(ids);

                            // USA IL PATTERN DI OBJECTEDITOR: prepareLights()
                            prepareLights();
                        }
                    });
                }
            });

            return commands;
        },
        [treeState, findNodeById, sceneEditor, prepareLights, showDialog, closeDialog, handleTreeDataChanged, nodes, removeNodes, askRemove]
    );

    // I comandi sono funzioni nuove a ogni render: l'effetto qui sotto non può dipenderne o
    // riparte all'infinito (manda un elemento nuovo all'Inspector, che rende di nuovo l'editor,
    // che rifà i comandi). Guarda la selezione, e i comandi li legge da qui.
    const subjectCommandsRef = useRef(subjectCommands);
    subjectCommandsRef.current = subjectCommands;

    // Con più di una riga presa, l'Inspector guarda il mucchio: stessa regola dell'albero degli
    // oggetti, stessi comandi del «…», che sono gli stessi di prima.
    useEffect(() => {
        if (!onInspectorChange || selectedIds.length < 2) return;

        const entries = selectedIds
            .map((id) => findNodeById(id))
            .filter((node): node is NonNullable<typeof node> => node != null)
            .map((node) => ({
                id: node.id,
                name: node.name,
                icon: lightIcon((node as any).lightType),
                // Una voce del badge prende quella sola, come un clic sulla sua riga.
                onPick: () => treeRef.current?.selectAndOpenOverlay(node.id)
            }));

        onInspectorChange(<SelectionInspector entries={entries} actions={subjectCommandsRef.current(selectedIds)} icon={<LightsRailIcon />} tone='light' />);
    }, [selectedIds, findNodeById, onInspectorChange, treeRef]);

    return (
        <SectionPanel
            selection={subjectCommands(selectedIds)}
            label={T._('Scene lights', 'Admin')}
            actions={tools}
            selectionHint={selectableIds.length > 1}
            filter={filter}
            onFilterChange={setFilter}
            searchPlaceholder={T._('Search lights…', 'Admin')}
        >
            <TreeComponent
                ref={treeRef}
                highlight={filter}
                treeState={treeStateForComponent}
                treeLabel={T._('Scene lights', 'Admin')}
                // L'OCCHIO DI UNA LUCE LA ACCENDE E LA SPEGNE (prototipo): una luce non si nasconde, si spegne.
                visibilityLabels={{ hide: T._('Turn off', 'Admin'), show: T._('Turn on', 'Admin') }}
                blinkRequest={blinkRequest}
                emptyIcon={<LightsIcon />}
                emptyMessage={filter.trim() ? T._('No light matches the search.', 'Admin') : T._('No lights in the scene yet. Add one to light it.', 'Admin')}
                showVisibilityIcon
                onToggleNodeVisibility={handleToggleLight}
                /* LA SPECIE IN CODA AL NOME: in un elenco di luci il tipo cambia da riga a riga, ed è
                   la prima cosa che si cerca scorrendo. Nel pannello si legge sotto il nome, ma per
                   saperlo bisogna aprirlo — una riga per volta. */
                rowNote={(node) => (node.lightType ? { text: LIGHT_KIND_SHORT[node.lightType] ?? '' } : null)}
                flatList
                disableOverlay={!!onInspectorChange}
                onSelectedComponentChange={onInspectorChange}
                /* Come negli oggetti: il nome si cambia sulla riga. Per una luce il nome lo tiene anche il
                   motore, che lo usa per ritrovarla, quindi glielo si dice. */
                onNodeRename={(node, name) => {
                    if (sceneEditor?.renameLight && node.data?.light?.id) sceneEditor.renameLight(node.data.light.id, name);
                    if (node.data?.light) node.data.light.name = name;

                    // IL NOME SI SCRIVE SULLO STATO, non addosso al nodo: scriverlo addosso cambia
                    // un oggetto che nessuno sta guardando — la riga si aggiorna al primo ridisegno
                    // che capita, e la colonna di destra non si accorge di niente.
                    //
                    // E NON SI RICOSTRUISCE L'ELENCO per cambiare una parola: la ricostruzione fa
                    // ripartire la risincronizzazione, che lascia cadere il pannello aperto mentre
                    // le righe si rifanno. È lo stesso difetto che avevano i materiali.
                    updateNode(node.id, { name });
                }}
                rowActions={(_node, ids) => subjectCommands(ids)}
                // UNA LUCE NON CONTIENE UNA LUCE: il trascinamento cambia l'ordine e basta (utente,
                // 2026-08-24). Il numero si scrive sulla luce — `metadata.displayOrder`, che è dove
                // sopravvive al salvataggio — e l'elenco si riordina da sé rileggendola.
                onNodeMoved={(nodeId, targetId) => {
                    const moving = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // L'ordine da cui si parte è quello che si VEDE, non quello in cui il motore 3D
                    // consegna le luci: è sul primo che l'utente ha appoggiato la riga.
                    const seen = [...nodes].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
                    const rest = seen.filter((node) => !moving.includes(node.id));
                    const taken = seen.filter((node) => moving.includes(node.id));

                    const at = rest.findIndex((node) => node.id === targetId);
                    rest.splice(at < 0 ? rest.length : at, 0, ...taken);

                    // IL NUMERO SI SCRIVE SULLA LUCE, perché è lì che sopravvive al salvataggio. Ma
                    // LA LISTA SI RIORDINA QUI, subito. Passare dal motore per vedere l'effetto —
                    // scrivi sulla luce, richiedi tutte le luci, ricostruisci i nodi, rileggi il
                    // numero — è una catena di quattro anelli, e basta che uno solo restituisca lo
                    // stesso array perché React non rirenderizzi niente: il trascinamento riusciva e
                    // non si vedeva. Una cosa che si trascina deve muoversi mentre la si lascia.
                    const reordered = rest.map((node, position) => {
                        const light = node.data?.light;

                        if (light) {
                            if (!light.metadata) light.metadata = {};
                            light.metadata.displayOrder = position;
                        }

                        return { ...node, displayOrder: position };
                    });

                    setNodes(reordered);
                    prepareLights();
                }}
            />
        </SectionPanel>
    );
};

export default LightEditor;
